import { Column, Row, Text } from "@/components";
import { GoalPanelPeriodToggle } from "@/components/dashboard/dashboard-stats/main-stats/GoalPanelPeriodToggle";

import {
  GOAL_PANEL_CONTAINER_CLASS,
  GOAL_PANEL_NARROW_LABEL_CLASS,
  GOAL_PANEL_NARROW_STACK_CLASS,
  GOAL_PANEL_VALUE_CLASS,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { GoalPeriod } from "@/types";

const FULL_PROGRESS_PERCENT = 100;

// A count-toward-goal panel: the period's total over its target, with how far
// is left and a bar for the gap. Used by submits and revenue.
export const GoalMetricPanel = ({
  barClassName,
  borderClassName,
  goal,
  goalLabel,
  goalsPeriod,
  isSelected,
  label,
  onGoalsPeriodChange,
  panelClassName,
  remainingLabel,
  textClassName,
  value,
  valueLabel,
}: {
  barClassName: string;
  borderClassName: string;
  goal: number;
  goalLabel: string;
  goalsPeriod: GoalPeriod;
  isSelected: boolean;
  label: string;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  panelClassName: string;
  remainingLabel: string;
  textClassName: string;
  value: number;
  valueLabel: string;
}) => {
  /* Helpers */
  const progressPercent =
    goal > 0 ? Math.min(FULL_PROGRESS_PERCENT, (value / goal) * 100) : 0;

  /* Render */
  const render = () => (
    <Column
      className={cn(
        GOAL_PANEL_CONTAINER_CLASS,
        "relative h-full w-full justify-between rounded-xl border p-4",
        borderClassName,
        panelClassName,
      )}
    >
      <Row className="items-start justify-between gap-2">
        <Text
          className={cn(
            "font-plex-mono text-[9px] font-semibold uppercase tracking-wider",
            textClassName,
          )}
        >
          {label}
        </Text>
      </Row>
      {isSelected ? (
        <GoalPanelPeriodToggle
          onPeriodChange={onGoalsPeriodChange}
          period={goalsPeriod}
        />
      ) : null}
      <Row className={cn("items-end gap-1.5", GOAL_PANEL_NARROW_STACK_CLASS)}>
        <Text
          className={cn(
            GOAL_PANEL_VALUE_CLASS,
            "whitespace-nowrap font-semibold tabular-nums leading-none tracking-tight",
            textClassName,
          )}
        >
          {valueLabel}
        </Text>
        <Text
          className={cn(
            GOAL_PANEL_NARROW_LABEL_CLASS,
            "pb-1 text-[10px] font-medium opacity-70",
            textClassName,
          )}
        >
          / {goalLabel}
        </Text>
      </Row>
      <Column className="gap-1.5">
        <Text className={cn("text-[10px] font-semibold", textClassName)}>
          {remainingLabel} left to goal
        </Text>
        {/* The track has to read against both panel states: a tinted selected
            panel and a plain white one. */}
        <Column
          className={cn(
            "h-2 w-full overflow-hidden rounded-full",
            isSelected ? "bg-white/70" : "bg-slate-100",
          )}
        >
          <Column
            className={cn("h-full rounded-full", barClassName)}
            style={{ width: `${progressPercent.toString()}%` }}
          />
        </Column>
      </Column>
    </Column>
  );

  return render();
};
